import React, { useCallback, useMemo } from "react";

import {
  Box,
  ListItem,
  AHSeparator,
  useSpaceSize,
  useLayoutContext,
  SectionHeader,
} from "arkh";
import { FormattedMessage, useIntl } from "react-intl";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { ConnectAdminScreenQuery } from "./__generated__/ConnectAdminScreenQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import {
  StoreAdminNavigationProp,
  StoreAdminRouteProps,
} from "../StoreAdmin/StoreAdminNavigation";
import { SectionList } from "react-native";
import { EntityHeader } from "@/src/components/EntityHeader";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { ConnectAdminScreenInboxListItem_inbox$key } from "./__generated__/ConnectAdminScreenInboxListItem_inbox.graphql";
import { ActionFooter } from "@/src/components/ActionFooter";
import { DialogButtonTeams } from "widgets/src/DialogButtonTeams";
import { TodoToolbarButtonTags } from "widgets";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type NavigationProp = StoreAdminNavigationProp<"ConnectAdmin">;

type Props = {
  navigation: NavigationProp;
  route: StoreAdminRouteProps<"ConnectAdmin">;
};

export function ConnectAdminScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {useCallback(
        (fetchKey) => (
          <ConnectAdmin {...props} fetchKey={fetchKey} />
        ),
        [props]
      )}
    </LazyQueryNetworkBoundrary>
  );
}
function ConnectAdmin(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<ConnectAdminScreenQuery>(
    graphql`
      query ConnectAdminScreenQuery {
        store {
          connectInboxes {
            id
            audience
            ...ConnectAdminScreenInboxListItem_inbox
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  useNavigationHeader({
    type: "BACK",
    defaultContentStyle: true,
    onBack: () => {
      props.navigation.goBack();
    },
  });

  const inboxes = useMemo(() => {
    return data.store.connectInboxes.filter(inbox => inbox)
  }, [data.store.connectInboxes]);

  const intl = useIntl();

  const sections = useMemo(() => {
    return [
      {
        title: intl.formatMessage({
          defaultMessage: "Employee Inboxes",
          description: "Connect Admin: Employee Inboxes Section Title",
        }),
        data: inboxes.filter(
          (inbox) => inbox.audience === "EMPLOYEES"
        ),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Customer Inboxes",
          description: "Connect Admin: Customer Inboxes Section Title",
        }),
        data: inboxes.filter(
          (inbox) => inbox.audience === "CUSTOMERS"
        ),
      },
    ].filter((section) => section.data.length > 0);
  }, [inboxes, intl]);

  const layout = useLayoutContext();
  const insets = useSafeAreaInsets();
  return (
    <>
      <SectionList
        contentInsetAdjustmentBehavior="automatic"
        contentContainerStyle={{
          paddingBottom: layout.space.medium + insets.bottom,
        }}
        sections={sections}
        ItemSeparatorComponent={Separator}
        stickySectionHeadersEnabled={false}
        style={{ flex: 1 }}
        renderSectionHeader={({ section }) => (
          <Box spaceTop="medium" spaceBottom="small">
            <SectionHeader>
              <SectionHeader.Title>{section.title}</SectionHeader.Title>
            </SectionHeader>
          </Box>
        )}
        renderItem={({ item, index, section }) => (
          <ConnectAdminScreenInboxListItem
            isFirst={index === 0}
            isLast={index === section.data.length - 1}
            onPress={() => {
              props.navigation.navigate("ConnectAdminInbox", {
                inboxId: item.id,
              });
            }}
            inbox={item}
          />
        )}
        ListHeaderComponent={
          <EntityHeader>
            <EntityHeader.Icon icon="connect" />
            <EntityHeader.Title>Connect</EntityHeader.Title>
            <EntityHeader.Button
              onPress={() => {
                props.navigation.navigate("ConnectAdminSiteAdmin");
              }}
            >
              <FormattedMessage
                defaultMessage="Settings"
                description="Connect Admin Config Settings Button Title"
              />
            </EntityHeader.Button>
          </EntityHeader>
        }
      />
      <ActionFooter
        primary={
          <ActionFooter.Primary
            icon="connect"
            title={
              <FormattedMessage
                defaultMessage="Create new inbox"
                description="Connect Admin: Create new inbox button title"
              />
            }
            onPress={() => {
              props.navigation.navigate("ConnectAdminCreateInit");
            }}
          />
        }
      />
    </>
  );
}
function Separator() {
  return <AHSeparator spaceHorizontal="medium" />;
}

function ConnectAdminScreenInboxListItem(props: {
  isFirst: boolean;
  isLast: boolean;
  onPress: () => void;
  inbox: ConnectAdminScreenInboxListItem_inbox$key;
}) {
  const inbox = useFragment(
    graphql`
      fragment ConnectAdminScreenInboxListItem_inbox on ConnectInbox {
        id
        audience
        customTitle
        todoCategory {
          title
        }
        teams {
          id
          name
          ...DialogButtonTeams_teams
        }
        tags {
          ...TodoToolbarButtonTags_tags
        }
      }
    `,
    props.inbox
  );

  const spacing = useSpaceSize("small");

  return (
    <Box spaceHorizontal="medium">
      <ListItem
        border="border-primary"
        isFirst={props.isFirst}
        isLast={props.isLast}
        onPress={props.onPress}
        right={<ListItem.NavArrow />}
        gap="xsmall"
      >
        <ListItem.Title>
          {inbox.customTitle ?? inbox.todoCategory?.title}
        </ListItem.Title>
        <Box style={{ flexDirection: "row", marginLeft: -spacing }}>
          <DialogButtonTeams teams={inbox.teams} isLocked />
          {(inbox.tags.length ?? 0) > 0 ? (
            <TodoToolbarButtonTags tags={inbox.tags} />
          ) : null}
        </Box>
      </ListItem>
    </Box>
  );
}
